import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Col, DatePicker, Form, Input, Row, Card } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash"

import { CustomSelect, PhoneInput } from "ui/Input"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import { LocationInfo } from "pagesV2/pro/Experiences/ListingLocation/LocationForm"
import useGeocoder from "modules/google/hooks/useGeocoder"
import usePredictions from "modules/google/hooks/usePredictions"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { CustomInputError } from "ui/CustomInputError"
import useFormErrors from "hooks/useAntdFormErrors"
import { cleanPastBooking } from "store/bookings"
import GroupManager from "pagesV2/pro/clients/ClientWindow/GroupManager"
import PropTypes from "prop-types"
import { getGroups } from "store/groups"
import { cleanClients } from "store/clients"

const ClientForm = ({ form }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const geocoder = useGeocoder(null)
  const { errors } = useFormErrors(form)
  const [selectPlace] = usePredictions()
  const [meetingLatLng, setMeetingLatLng] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [avatar, setAvatar] = useState(() => form.getFieldValue("avatar") || null)
  const [selectedGroups, setSelectedGroups] = useState([])
  const clients = useSelector((state) => state.clients.client)

    useEffect(() => {
      return () => dispatch(cleanPastBooking())
    }, [dispatch])

  const genderOptions = [
    { label: t("settings.gender.male"), value: "male" },
    { label: t("settings.gender.female"), value: "female" }
  ]

  const changeHandlers = ({ target: { name, value } }) => {
    form.setFieldsValue({ [name]: value })
  }

  const debounceCoordinatesToAddress = useCallback(
    debounce(
      (location) =>
        geocoder({ location }, (place) => {
          changeHandlers({ target: { name: "address", value: place.formatted_address } })
        }),
      200
    ),
    [geocoder]
  )

  const placeChangedHandler = useCallback(
    ({ target: { coordinates, value } }) => {
      if (coordinates) debounceCoordinatesToAddress(coordinates)
      if (value) {
        setMeetingLatLng(value)
      }
    },
    [debounceCoordinatesToAddress]
  )

  useEffect(() => {
    const address = clients.user;
    if (address?.address_line_1) {
      setMeetingLatLng(address.address_line_1)
    }
    if (address.city || address.state || address.country || address.zip_code) {
      setManualAddress(true)
      setMeetingLatLng(`${address.city}, ${address.state}, ${address.country}, ${address.zip_code}`)
    }
  }, [clients])

  useEffect(() => {
    form.setFieldsValue({
      manualAddress: manualAddress
    })
  }, [manualAddress])

  useEffect(() => {
    if (!meetingLatLng) {
      form.setFieldsValue({ address: "" })
    }
  }, [meetingLatLng, form])

  const onUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target.result
      setAvatar(base64)
      form.setFieldsValue({ avatar: file })
    }
    reader.readAsDataURL(file)
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      onUpload(file)
    }
  }

  // Fetch groups on component mount
  useEffect(() => {
    dispatch(getGroups())

    return () => {
      dispatch(cleanClients())
    }
  }, [dispatch])

  useEffect(() => {
    const clientGroups = form.getFieldValue("client_groups") || []
    // Create a deep copy to prevent modifying the original form data
    const groupsCopy = clientGroups.map(group => ({ ...group }));
    setSelectedGroups(groupsCopy);
  }, [form.getFieldValue("client_groups")]);

  const onInputChange = (value) => {
    form.setFieldsValue({ phone_number: value });
  }; const handleSelectedGroupsChange = (updatedGroups) => {
    const originalGroups = clients.client_groups
    let finalGroups = [...updatedGroups]

    // Keep track of removed groups from originalGroups
    originalGroups.forEach(origGroup => {
      const origGroupId = origGroup?.group_id || origGroup?.id;
      const isRemoved = !updatedGroups.some(updGroup => {
        const updGroupId = updGroup?.group_id || updGroup?.id;
        return updGroupId === origGroupId;
      });

      // If group was in original but not in updated, mark it as destroyed
      if (isRemoved && origGroupId) {
        finalGroups.push({
          ...origGroup,
          _destroy: true
        })
      }
    });
    form.setFieldsValue({ client_groups: finalGroups });
  };

  const handleNameKeyDown = (e) => {
    const key = e.key;
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (!/^[A-Za-z]$/.test(key) && !allowedKeys.includes(key)) {
      e.preventDefault();
    }
  };

  return (
    <Card className="mb-3xl" layout="vertical">
      <h4 className="mb-s"> {t("client.personal_info")}</h4>
      <Form.Item name="avatar" className="mb-l">
        <AddAvatar avatar={avatar} handleImageChange={handleImageChange} />
      </Form.Item>
      <Row gutter={[20, 0]}>
        <Col span={24}>
          <Form.Item label={t("client.filters.groups")} name="client_groups">
            <GroupManager selectedGroups={selectedGroups} moduleName="client"
              onSelectedGroupsChange={handleSelectedGroupsChange} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="first_name"
            label={t("client.first_name")}
            rules={[{ required: true, message: t("client.error_messages.first_name_required") }
            ]}
          >
            <Input onKeyDown={handleNameKeyDown} placeholder={t("client.first_name")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="last_name"
            label={t("client.last_name")}
            rules={[{ required: true, message: t("client.error_messages.last_name_required") }]}
          >
            <Input
              onKeyDown={handleNameKeyDown} placeholder={t("client.last_name")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="email"
            label={t("client.email")}
            rules={[
              { required: true, message: t("client.error_messages.email_required") },
              { type: "email", message: t("client.error_messages.email_invalid") }
            ]}
          >
            <Input placeholder={t("client.email")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            label={t("sign_up.phone_number")}
            name="phone_number"
            required={true}
            rules={[{ required: true, message: t("form_validation.phone_number_invalid") }]}
            help={errors?.phone_number ? <CustomInputError errorKey={errors.phone_number} showLinkText={false} /> : null}
          >
            <PhoneInput initialValue={clients?.user?.phone_number} onChange={onInputChange} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item name="gender" label={t("settings.gender_label")}>
            <CustomSelect options={genderOptions} placeholder={t("settings.gender_label")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item name="birthday" label={t("client.birthday")}>
            <DatePicker
              style={{ width: "100%", height: "44px" }}
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              disabledDate={(current) => current && current > Date.now()}
            />
          </Form.Item>
        </Col>
      </Row>
      {!manualAddress ? (
        <Form.Item label={t("client.address")} name="address_line_1">
          <GooglePlacesNewInput
            placeholder="Enter your address"
            bsSize="sm"
            onChange={placeChangedHandler}
            dropDownMenuClassName="mt-2 shadow-sm"
            resetIfChanged
            meetingLatLng={meetingLatLng}
            manualAddress={manualAddress}
            setManualAddress={setManualAddress}
            setMeetingLatLng={setMeetingLatLng}
          />
        </Form.Item>
      ) : (
        <Form.Item name="manualAddress">
          <LocationInfo
            isRequired={false}
            screenName="Client"
            form={form}
            t={t}
            setCenterLatLng={null}
            setManualAddress={setManualAddress}
            setMeetingLatLng={setMeetingLatLng}
            selectPlace={selectPlace}
          />
        </Form.Item>
      )}
    </Card>
  )
}

ClientForm.propTypes = {
  form: PropTypes.object.isRequired
}

export default ClientForm
